import { createRouter, createWebHistory } from "vue-router";
import { isUpdatePending } from "@/services/releaseNotesService";
import LoginPage from "../views/mainHomePages/LoginPage.vue";
import NoAccess from "../views/mainHomePages/NoAccess.vue";
import DashboardPage from "../views/mainHomePages/DashboardPage.vue";
import store from "../store";
import { PENDING_SUPPORT_LINK_KEY } from "@/utils/supportCases.js";
import {
  ensureAuthBootstrapped,
  clearAppAuthState,
  resetAuthBootstrap,
} from "@/services/authSession.js";

const PUBLIC_ROUTE_NAMES = new Set(["LoginPage", "DashboardPage", "NoAccess"]);

const routes = [
  {
    path: "/",
    name: "LoginPage",
    component: LoginPage,
  },
  {
    path: "/DashboardPage",
    name: "DashboardPage",
    component: DashboardPage,
  },
  {
    path: "/LandingPage",
    name: "LandingPage",
    component: () =>
      import(
        /* webpackChunkName: "LandingPage" */ "../views/mainHomePages/LandingPage.vue"
      ),
    children: [
      {
        // Not in RouterList.json / role modules on purpose: access is gated
        // by the backend (getSupportAccess), and email deep links
        // (/SupportCases?case=<ticket_id>) must work for every user.
        path: "/SupportCases",
        name: "SupportCases",
        component: () =>
          import(
            /* webpackChunkName: "SupportCases" */ "../views/ChildHomePages/SupportCases.vue"
          ),
      },
      {
        // Open to every signed-in user (not a role module), like SupportCases.
        path: "/TrainingVideos",
        name: "TrainingVideos",
        component: () =>
          import(
            /* webpackChunkName: "TrainingVideos" */ "../views/ChildHomePages/TrainingVideos.vue"
          ),
      },
      {
        path: "/MetricsOverview",
        name: "MetricsOverview",

        component: () =>
          import(
            /* webpackChunkName: "MetricsOverview" */ "../views/ChildHomePages/MetricsOverview.vue"
          ),
      },
      {
        path: "/CallMetrics",
        name: "CallMetrics",

        component: () =>
          import(
            /* webpackChunkName: "CallMetrics" */ "../views/ChildHomePages/CallMetrics.vue"
          ),
      },
      {
        path: "/Dashboard",
        name: "Dashboard",

        component: () =>
          import(
            /* webpackChunkName: "Dashboard" */ "../views/ChildHomePages/Dashboard.vue"
          ),
      },
      {
        path: "/Campaigns",
        name: "Campaigns",

        component: () =>
          import(
            /* webpackChunkName: "Campaigns" */ "../views/ChildHomePages/Campaigns.vue"
          ),
      },
      {
        path: "/Lists",
        name: "Lists",

        component: () =>
          import(
            /* webpackChunkName: "Lists" */ "../views/ChildHomePages/Lists.vue"
          ),
      },
      {
        path: "/Clients",
        name: "Clients",

        component: () =>
          import(
            /* webpackChunkName: "Clients" */ "../views/ChildHomePages/Clients.vue"
          ),
      },
      {
        path: "/Products",
        name: "Products",

        component: () =>
          import(
            /* webpackChunkName: "Products" */ "../views/ChildHomePages/Products.vue"
          ),
      },
      {
        path: "/Roles",
        name: "Roles",

        component: () =>
          import(
            /* webpackChunkName: "Roles" */ "../views/ChildHomePages/Roles.vue"
          ),
      },
      {
        path: "/Users",
        name: "Users",

        component: () =>
          import(
            /* webpackChunkName: "Users" */ "../views/ChildHomePages/Users.vue"
          ),
      },
      {
        path: "/Admins",
        name: "Admins",

        component: () =>
          import(
            /* webpackChunkName: "Users" */ "../views/ChildHomePages/Admins.vue"
          ),
      },
      {
        path: "/Agents",
        name: "Agents",

        component: () =>
          import(
            /* webpackChunkName: "Users" */ "../views/ChildHomePages/Agents.vue"
          ),
      },
      {
        path: "/Locations",
        name: "Locations",

        component: () =>
          import(
            /* webpackChunkName: "Locations" */ "../views/ChildHomePages/Locations.vue"
          ),
      },
      {
        path: "/Teams",
        name: "Teams",

        component: () =>
          import(
            /* webpackChunkName: "AgentGroup" */ "../views/ChildHomePages/Teams.vue"
          ),
      },
      {
        path: "/FieldRepository",
        name: "FieldRepository",

        component: () =>
          import(
            /* webpackChunkName: "FieldRepository" */ "../views/ChildHomePages/FieldRepository.vue"
          ),
      },
      {
        path: "/OwnForm",
        name: "OwnForm",

        component: () =>
          import(
            /* webpackChunkName: "OwnForm" */ "../views/ChildHomePages/OwnForm.vue"
          ),
      },
      {
        path: "/OwnData",
        name: "OwnData",

        component: () =>
          import(
            /* webpackChunkName: "OwnData" */ "../views/ChildHomePages/OwnData.vue"
          ),
      },
      {
        path: "/PBTB",
        name: "PBTB",

        component: () =>
          import(
            /* webpackChunkName: "PBTB" */ "../views/ChildHomePages/PBTB.vue"
          ),
      },
      {
        path: "/DataModificationRequests",
        name: "DataModificationRequests",

        component: () =>
          import(
            /* webpackChunkName: "DataModificationRequests" */ "../views/ChildHomePages/DataModificationRequests.vue"
          ),
      },
      {
        path: "/DNDList",
        name: "DNDList",

        component: () =>
          import(
            /* webpackChunkName: "DNDList" */ "../views/ChildHomePages/DNDList.vue"
          ),
      },
      {
        path: "/DeDuplicates",
        name: "DeDuplicates",

        component: () =>
          import(
            /* webpackChunkName: "DeDuplicates" */ "../views/ChildHomePages/DeDuplicates.vue"
          ),
      },
      {
        path: "/DispositionCodes",
        name: "DispositionCodes",

        component: () =>
          import(
            /* webpackChunkName: "DispositionCodes" */ "../views/ChildHomePages/DispositionCodes.vue"
          ),
      },
      {
        path: "/AuditLogs",
        name: "AuditLogs",

        component: () =>
          import(
            /* webpackChunkName: "DispositionCodes" */ "../views/ChildHomePages/AuditLogs.vue"
          ),
      },
      {
        path: "/Settings",
        name: "Settings",

        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "../views/ChildHomePages/Settings.vue"
          ),
      },
      // {
      //   path: "/CallAgentReports",
      //   name: "CallAgentReports",

      //   component: () => import(/* webpackChunkName: "CallAgentReports" */ "../views/ChildHomePages/CallAgentReports.vue"),
      // },
      {
        path: "/ProductMaster",
        name: "ProductMaster",

        component: () =>
          import(
            /* webpackChunkName: "ProductMaster" */ "../views/ChildHomePages/ProductMaster.vue"
          ),
      },
      {
        path: "/CallRecordings",
        name: "CallRecordings",

        component: () =>
          import(
            /* webpackChunkName: "CallRecordings" */ "../views/ChildHomePages/CallRecordings.vue"
          ),
      },
      {
        path: "/Reports",
        name: "Reports",

        component: () =>
          import(
            /* webpackChunkName: "CallRecordings" */ "../views/ChildHomePages/Reports.vue"
          ),
      },
      {
        path: "/CurateData",
        name: "CurateData",

        component: () =>
          import(
            /* webpackChunkName: "CurateData" */ "../views/ChildHomePages/CurateData.vue"
          ),
      },
      // {
      //   path: "/Dashboard-ATDC",
      //   name: "Dashboard-ATDC",
      //   component: () => import(/* webpackChunkName: "Help" */ "../views/ChildHomePages/DashboardATDC.vue"),
      // },
    ],
  },
  {
    path: "/NoAccess",
    name: "NoAccess",
    component: NoAccess,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  // Forced upgrade: a newer build is live (see ReleaseNotesBanner), so turn
  // this navigation into a full page load - the user lands on the page they
  // asked for, running the new build, instead of continuing on stale code.
  if (isUpdatePending()) {
    window.location.assign(to.fullPath);
    return false;
  }

  const isPublic = PUBLIC_ROUTE_NAMES.has(to.name);

  const auth = await ensureAuthBootstrapped();

  if (to.name === "LoginPage" && auth.status === "AUTHENTICATED") {
    return { name: "LandingPage" };
  }

  if (!isPublic && auth.status === "UNAUTHENTICATED") {
    console.warn("[auth] redirect_login", {
      reason: "router_guard_unauthenticated",
      to: to.fullPath,
    });
    if (to.name === "SupportCases") {
      // Remember the support-case deep link (from a support email) so the
      // Landing page can reopen it right after sign-in.
      try {
        sessionStorage.setItem(PENDING_SUPPORT_LINK_KEY, to.fullPath);
      } catch (e) {
        // storage disabled - the user just lands on their default page
      }
    }
    clearAppAuthState(store);
    resetAuthBootstrap();
    return { name: "LoginPage" };
  }

  return true;
});

// Recover from stale/missing lazy chunks (e.g. after deploy) without leaving the UI stuck.
router.onError((error, to) => {
  const message = error?.message || "";
  const isChunkError =
    error?.name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message);

  if (!isChunkError) return;

  const key = "chunk_reload_" + (to?.fullPath || window.location.pathname);
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, "1");
    window.location.reload();
  }
});

export default router;
